import { useQuery } from '@apollo/client'
import { Close, ExpandLess, ExpandMore } from '@mui/icons-material'
import {
    Alert,
    Box,
    CircularProgress,
    Collapse,
    Drawer,
    IconButton,
    Stack,
    Typography,
} from '@mui/material'
import { type FC, useEffect, useState } from 'react'
import { BotAuditConversation } from './BotAudit.gql'
import { DevAffordances } from './DevAffordances'
import { LogPanel, type LogRow } from './LogPanel'
import { MessageTimeline } from './MessageTimeline'

interface ThreadDrawerProps {
    conversationId: string | null
    onClose: () => void
}

export const ThreadDrawer: FC<ThreadDrawerProps> = ({
    conversationId,
    onClose,
}) => {
    const [rawJsonMode, setRawJsonMode] = useState(false)
    const [logsOpen, setLogsOpen] = useState(false)

    // Reset back to Timeline when the user jumps to a different conversation
    // so JSON mode doesn't silently persist across threads.
    useEffect(() => {
        setRawJsonMode(false)
    }, [conversationId])

    const { data, loading, error } = useQuery(BotAuditConversation, {
        variables: { id: conversationId ?? '' },
        skip: !conversationId,
        fetchPolicy: 'cache-and-network',
    })

    const conv = data?.botAuditConversation
    const userLabel = conv?.user
        ? `${conv.user.firstName ?? ''} ${conv.user.lastName ?? ''}`.trim() ||
          conv.user.email ||
          conv.slackUserId
        : conv?.slackUserId ?? ''

    return (
        <Drawer
            anchor="right"
            open={!!conversationId}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: { xs: '100%', sm: 640 },
                    maxWidth: '100%',
                    // flex column + overflow:hidden lets the inner scroll Box constrain itself.
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                },
                // Data-testid on the Paper (not the Drawer) because Drawer renders via a portal.
                'data-testid': 'bot-audit-thread-drawer',
            }}
        >
            <Stack
                sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}
                spacing={0.5}
            >
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography
                        variant="subtitle1"
                        sx={{ flex: 1, fontWeight: 600 }}
                    >
                        Conversation
                    </Typography>
                    <IconButton
                        size="small"
                        onClick={onClose}
                        aria-label="Close conversation"
                        data-testid="bot-audit-thread-drawer-close"
                    >
                        <Close fontSize="small" />
                    </IconButton>
                </Stack>
                {conv ? (
                    <Stack
                        direction="row"
                        spacing={2}
                        sx={{ fontSize: '0.75rem', color: 'text.secondary' }}
                    >
                        <Box>{userLabel}</Box>
                        <Box>·</Box>
                        <Box>#{conv.slackChannelId}</Box>
                        <Box>·</Box>
                        <Box>{new Date(conv.createdAt).toLocaleString()}</Box>
                        <Box>·</Box>
                        <Box>
                            {conv.messages.length} message
                            {conv.messages.length === 1 ? '' : 's'}
                        </Box>
                    </Stack>
                ) : null}
            </Stack>

            {conversationId && conv ? (
                <DevAffordances
                    conversationId={conversationId}
                    rawJsonMode={rawJsonMode}
                    onToggleRawJson={setRawJsonMode}
                />
            ) : null}

            {/* key={conversationId} resets scroll when switching threads. */}
            <Box
                key={conversationId ?? 'empty'}
                sx={{ flex: 1, overflowY: 'auto', p: 2 }}
                data-testid="bot-audit-thread-message-list"
            >
                {error ? <Alert severity="error">{error.message}</Alert> : null}
                {loading && !conv ? (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            py: 4,
                        }}
                    >
                        <CircularProgress size={20} />
                    </Box>
                ) : conv ? (
                    rawJsonMode ? (
                        // This is the Apollo-normalized shape (includes __typename on nested objects),
                        // not the raw API payload. That's generally what you want for debugging.
                        <Box
                            component="pre"
                            sx={{
                                fontFamily: 'monospace',
                                fontSize: '0.75rem',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                m: 0,
                            }}
                            data-testid="bot-audit-thread-raw-json"
                        >
                            {JSON.stringify(conv, null, 2)}
                        </Box>
                    ) : (
                        <>
                            <MessageTimeline messages={conv.messages} />
                            <Box
                                sx={{
                                    mt: 3,
                                    pt: 2,
                                    borderTop: 1,
                                    borderColor: 'divider',
                                }}
                            >
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={0.5}
                                >
                                    <IconButton
                                        size="small"
                                        sx={{ p: 0.25 }}
                                        onClick={() => setLogsOpen((o) => !o)}
                                        aria-expanded={logsOpen}
                                        aria-label={
                                            logsOpen
                                                ? 'Collapse logs'
                                                : 'Expand logs'
                                        }
                                    >
                                        {logsOpen ? (
                                            <ExpandLess fontSize="small" />
                                        ) : (
                                            <ExpandMore fontSize="small" />
                                        )}
                                    </IconButton>
                                    <Box
                                        sx={{
                                            fontSize: '0.7rem',
                                            textTransform: 'uppercase',
                                            fontWeight: 500,
                                            color: 'text.secondary',
                                        }}
                                    >
                                        Logs ({conv.logs.length})
                                    </Box>
                                </Stack>
                                <Collapse in={logsOpen}>
                                    <Box
                                        sx={{ mt: 1 }}
                                        data-testid="bot-audit-thread-logs"
                                    >
                                        <LogPanel
                                            logs={conv.logs as LogRow[]}
                                            emptyText="No logs for this conversation."
                                        />
                                    </Box>
                                </Collapse>
                            </Box>
                        </>
                    )
                ) : !loading && data ? (
                    <Typography
                        color="text.secondary"
                        sx={{ textAlign: 'center', py: 4 }}
                    >
                        Conversation not found.
                    </Typography>
                ) : null}
            </Box>
        </Drawer>
    )
}
